import { APP_NAME } from './config'

export const STRINGS = {
  nav: {
    exam:  'Exam',
    manage: 'Manage',
  },

  hero: {
    badge:       'AI-Powered Exam Platform',
    headline1:   'Learn. Practice.',
    headline2:   'Certify. Grow.',
    descriptionAdmin: `Upload your PDFs — textbooks, previous year papers, study notes — and ${APP_NAME} generates adaptive exams with instant feedback and topic-level coaching.`,
    descriptionUser:  `Enter the exam code provided by your organisation to take your exam. Get instant AI feedback and track your progress with topic-level coaching.`,
    descriptionStandalone: `Practice with AI-powered exams, get instant feedback, and grow with topic-level coaching.`,
    features: [
      { icon: '📄', text: 'Upload any PDF — books, papers, notes'  },
      { icon: '🤖', text: 'AI generates questions instantly'        },
      { icon: '📊', text: 'Topic-level performance analysis'        },
      { icon: '🎓', text: 'Trials & exam modes'                     },
    ],
    featuresUser: [
      { icon: '🎯', text: 'Exams assigned by your organisation'     },
      { icon: '🤖', text: 'Instant AI feedback on every answer'     },
      { icon: '📊', text: 'Topic-level performance analysis'        },
      { icon: '📈', text: 'Track your progress over time'           },
    ],
  },

  modes: {
    practice:        'Trials',
    virtual:         'Exams',
  },

  setup: {
    modeLabel:       'Mode',
    difficultyLabel: 'Difficulty',
    breakdownLabel:  'Question Breakdown',
    totalLabel:      'total',
    startPractice:   'Start Trial',
    startVirtual:    'Start Exam',
    buildingExam:    'Building exam…',
    noQuestions:     'Add at least 1 question to start',
    bankToggle:      'questions in bank',
    termsCheckbox:       'I accept exam terms and conditions',
    termsAgree:          'I have read and agree to the terms and conditions',
    activeSessionError:  'You already have an active exam session.',
  },

  bankBuilding: {
    title:    'Building Question Bank',
    subtitle: 'Generating questions across all syllabus units. Please keep this page open.',
  },

  grading: {
    title:    'Grading your exam',
    subtitle: 'Building your personalised performance report…',
    steps: [
      'Reading your answers…',
      'Evaluating MCQs & True/False…',
      'Grading short answers…',
      'Computing topic breakdown…',
      'Generating study report…',
    ],
  },

  results: {
    autoSubmitted:    '⏰ Time ran out — exam was auto-submitted.',
    autoSubmittedTab:     '⚠ Exam auto-submitted — switching tabs or windows is not allowed during the exam.',
    autoSubmittedSession: '⚠ Exam auto-submitted — your session was signed in on another device.',
    autoSubmittedScreenShare: '⚠ Exam auto-submitted — screen sharing is not allowed during the exam.',
    topicPerformance: 'Topic Performance',
    studyReport:      'Personalised Study Report',
    reviewAnswers:    'Review Answers',
    tryAgain:         'Try Again →',
    changeDifficulty: 'Change Difficulty',
    passBadge:        'PASS ✓',
    failBadge:        'FAIL ✗',
    cutoffLabel:      'cutoff',
    noResults:        'No results found.',
    backToSetup:      '← Back to Setup',
    passedTip:        'Great work! Review any weak topics above.',
    failedTip:        'Keep practising — review topics below 50%.',
    virtualLabel:     '⏱ Exam',
    practiceLabel:    '📖 Trial',
  },

  admin: {
    // List view
    panelLabel:       'Admin Panel',
    title:            'Exams',
    subtitle:         'Create and manage exams. Each exam has its own set of PDF sources.',
    newExam:          '+ New Exam',
    loading:          'Loading…',
    searchPlaceholder:'Search exams…',
    clearSearch:      'Clear search',
    noExamsTitle:     'No exams yet',
    noExamsDesc:      'Create your first exam to start uploading PDFs and generating questions.',
    createFirst:      '+ Create First Exam',
    noSearchResults:  (q) => `No exams match "${q}"`,

    // Exam card
    noDescription:    'No description',
    statPdfs:         (n) => `PDF${n !== 1 ? 's' : ''}`,
    statSize:         'Size',
    statCreated:      'Created',
    deleteExamTitle:  'Delete exam',
    deleteExamConfirm:(name) => `Delete "${name}" and all its PDFs? This cannot be undone.`,

    // Sort options
    sortNewest:       'Newest',
    sortOldest:       'Oldest',
    sortAZ:           'A → Z',
    sortZA:           'Z → A',
    sortMostPdfs:     'Most PDFs',

    // Create exam form
    createTitle:      'New Exam',
    examNameLabel:    'Exam Name *',
    examNamePlaceholder: 'e.g. Financial Markets Fundamentals',
    descLabel:        'Description (optional)',
    descPlaceholder:  'Brief description of this exam',
    pdfsOptionalLabel:'PDF Sources *',
    creating:         'Creating…',
    createBtn:        'Create Exam',
    cancel:           'Cancel',

    // Detail view — header
    examLabel:        'Exam',
    editBtn:          '✎ Edit',
    editTitle:        'Edit name & description',
    deleteBtn:        '✗ Delete',
    deleteTitle:      'Delete exam',
    editExamLabel:    'Edit Exam',
    editNameLabel:    'Exam Name *',
    editDescLabel:    'Description',
    editDescPlaceholder: 'Optional description',
    saving:           'Saving…',
    saveChanges:      'Save Changes',
    deleting:         'Deleting…',
    deleteExamBtn:    'Delete Exam',

    // Detail view — stats
    statTextbookPdfs:  'Syllabus/Textbook PDFs',
    statPatternPdfs:   'Sample Questions',
    statCreatedLabel:  'Created',

    // Detail view — PDF sections
    textbookPdfsLabel: 'Syllabus/Textbook PDFs (15 MB) *',
    textbookPdfsDesc:  'Textbook or syllabus',
    textbookPdfsInfo:  'Upload your textbook or syllabus here. The AI reads these to generate exam questions covering all topics.',
    patternPdfsLabel:  'Sample Questions (5 MB)',
    patternPdfsDesc:   'Previous papers or sample questions',
    patternPdfsInfo:   'Upload previous year papers or sample question sets. The AI uses these to understand the question style, formatting, and question pattern. You can further fine-tune the pattern using the Question Patterns config below.',
    noPdfsTitle:       'No PDFs yet. Upload below to get started.',
    deletePdfConfirm:  (name) => `Delete "${name}"? This cannot be undone.`,
    deleteBtn2:        'Delete',

    // Question settings
    questionSettingsLabel: 'Question Settings',
    questionTypesLabel:    'Question Patterns',
    questionTypeColLabel:  'Question Patterns',
    totalQuestionsLabel:   'Total Questions',
    saveSettingsBtn:       'Save Settings',
    savingSettings:        'Saving…',
    toastSettingsSaved:    'Settings saved.',

    // Upload zone
    dropActive:       'Drop PDFs here',
    dropIdle:         'Click or drag & drop PDFs',
    dropSub:          'Multiple files supported',
    uploadFailed:     'Failed',
    uploadingLabel:   'Uploading…',
    uploadBtn:        (n) => `↑ Upload ${n} file${n > 1 ? 's' : ''}`,
    clearAll:         'Clear all',
    onlyPdf:          'Only PDF files are accepted.',
    sizeLimit:        (names, plural, mb) => `${names} exceed${plural ? '' : 's'} the ${mb} MB limit.`,

    // Knowledge base
    kbLabel:          'Knowledge Base',
    reloadTitle:      'Reload Engine',
    reloadDesc:       'Forces the RAG engine to re-read all PDFs after adding or removing sources.',
    reloadBtn:        'Reload Engine',
    resetTitle:       'Reset Question Bank',
    resetDesc:        'Clears all generated questions. A full rebuild runs on the next exam start.',
    resetBtn:         'Reset Bank',
    working:          'Working…',

    // Toasts
    toastExamCreated: () => 'Exam created.',
    toastExamUpdated: 'Exam updated.',
    toastExamDeleted: 'Exam deleted.',
    toastPdfDeleted:  (name) => `Deleted ${name}`,
    toastUploaded:    (n) => `${n} file${n > 1 ? 's' : ''} uploaded.`,
    toastUploadFail:  (n) => `${n} file${n > 1 ? 's' : ''} failed.`,
    toastSettingsSaved: 'Settings saved.',
    toastReloaded:    'Engine will reload on next visit.',
    toastBankCleared: 'Question bank cleared.',

    // Back
    backToExams:      '← All Exams',

    // Remove file tooltip
    removeFile:       'Remove file',

    // Plan-gated features
    lockedBadge:            '🔒 Locked',
    bloomDistributionLabel: "Bloom's Taxonomy",
    practiceMultiplierLabel: 'Trial Question Pool Size',
    practiceMultiplierDesc:  "Multiplies each Trial difficulty pool's question count. Use the +/- control on the right to increase or decrease the multiplier.",

    // Trials settings
    trialsEnabledLabel:  'Enable Trials',
    trialsEnabledDesc:   'Let students practice with instant feedback before the actual exam.',
    trialLevelLockLabel: 'Allow skip Trials',
    trialLevelLockDesc:  'Students can jump to any difficulty level freely. Turn off to require passing Beginner before Intermediate, then Advanced, then Expert.',
    showQuestionTypeLabel: 'Show question type on each question',
    showQuestionTypeDesc:  "Labels every question “Only one option is selectable” or “Multiple options are selectable” during Trials and the real Exam, so examinees don't mistake one for the other.",

    // Share messages (WhatsApp/Email) — single source of truth. Previously
    // duplicated by hand across examDetail.jsx, adminPage.jsx and
    // superAdminPage.jsx; every wording tweak needed finding and editing all
    // of them. `origin` is shareOrigin()'s result, passed in by the caller.
    inviteCodeMessage: (orgName, origin, code) =>
      `Hi! You've been invited to join ${orgName || 'our organisation'} on RADTAR SkillAssess.\n\nHere's how to get started:\n1. Visit ${origin}\n2. Register as an Examinee using invite code: ${code}\n3. Learn. Practice and improve your skills\n\nSee you there!`,
    inviteCodeSubject: (orgName) => `You're invited to join ${orgName || 'us'} on RADTAR SkillAssess`,

    examCodeMessage: (examName, origin, code, orgName) =>
      `Hi! You've been invited to take ${examName} on RADTAR SkillAssess platform.\n\nTo attend the examination:\n1. Visit: ${origin}/\n2. Create / Log in to your account.\n3. Enter the Exam Code: ${code}\n4. Start your assessment and examination.\n\nEnsure you have a stable internet connection and complete the examination within the specified time.\n\nCheck user guide for more details: ${origin}/guide\n\nAll the best for your examination!\n\n${orgName || 'RADTAR SkillAssess'}\nLearn. Practice. Certify. Grow.`,
    examCodeSubject: (examName) => `You're invited to take "${examName}" — RADTAR SkillAssess`,
  },

  toast: {
    reloaded:    'Engine will reload on next visit.',
    bankCleared: 'Question bank cleared.',
    deleted:     'Deleted',
    saved:       'Saved',
  },

  feedback: {
    correct:         '✓ Correct',
    incorrect:       '✗ Incorrect',
    answeredLabel:   '✓ answered',
    correctAnswer:   'Answer:',
    modelAnswer:     'Model answer:',
    unansweredWarn:  (n) => `⚠ ${n} unanswered`,
    submitEarly:     (answered, total) => `Submit early (${answered}/${total} answered)`,
  },

  auth: {
    loginTitle:       'Welcome back',
    loginSubtitle:    'Sign in to your account',
    loginCta:         'Sign In →',
    loginLoading:     'Signing in…',
    loginSwitch:      "Don't have an account?",
    loginSwitchLink:  'Create one',

    registerTitle:    'Create your account',
    registerSubtitle: 'Start using RADTAR SkillAssess today',
    registerCta:      'Create Account →',
    registerLoading:  'Creating account…',
    registerSwitch:   'Already have an account?',
    registerSwitchLink: 'Sign in',

    emailLabel:       'Email',
    emailPlaceholder: 'you@company.com',
    passwordLabel:    'Password',
    passwordPlaceholder: '••••••••',
    nameLabel:        'Name',
    namePlaceholder:  'Your name',
    confirmLabel:     'Confirm Password',
    confirmPlaceholder: '••••••••',

    logout:           'Sign out',

    inviteCodeLabel:       'Invite Code (optional)',
    inviteCodePlaceholder: 'e.g. SAN1001',
    inviteCodeHint:        'Have a code from your organisation? Enter it to access their exams.',
    inviteCodeTooltipWith:    { label: 'With invite code', desc: 'you join an organisation and will need their approval before you can log in.' },
    inviteCodeTooltipWithout: { label: 'Without invite code', desc: 'you won\'t be linked to any organisation and can log in immediately after email verification.' },
  },

  virtual: {
    noFeedback:  'No feedback until submission',
    remaining:   'remaining',
    submit:      'Submit →',
    answered:    (a, t) => `${a}/${t} answered`,
  },

  terms: {
    title:        'Terms & Conditions',
    effective:    'Effective Date: July 6, 2026',
    intro:        `Welcome to ${APP_NAME} ("the Platform"), operated by RADTAR Innovations Pvt Ltd ("we", "us", or "our"). By registering or using the Platform, you agree to be bound by these Terms & Conditions. Please read them carefully.`,
    sections: [
      {
        heading: '1. Acceptance of Terms',
        body: [
          `By creating an account or accessing any part of ${APP_NAME}, you confirm that you have read, understood, and agree to these Terms & Conditions and our Privacy Policy. If you do not agree, you must not use the Platform.`,
        ],
      },
      {
        heading: '2. Description of Service',
        body: [
          `${APP_NAME} is an AI-powered exam and assessment platform that allows Organisations to create, manage, and deliver exams to their users, and allows Examinees to take exams and track their progress.`,
          'The Platform provides features including PDF upload, AI-generated question banks, performance analytics, and result tracking.',
        ],
      },
      {
        heading: '3. User Accounts',
        body: [
          'You must provide accurate, current, and complete information during registration. You are responsible for maintaining the confidentiality of your account credentials.',
          'You must not share your account with others or allow unauthorised access. You are liable for all activity that occurs under your account.',
          'We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent or harmful behaviour.',
        ],
      },
      {
        heading: '4. Organisation Accounts',
        body: [
          'Organisation accounts must be approved by RADTAR Innovations Pvt Ltd before access is granted. Approval may be refused at our discretion.',
          'Organisations are responsible for ensuring that all users they onboard comply with these Terms. The invite code issued to an Organisation must not be shared publicly or misused.',
          'RADTAR Innovations Pvt Ltd reserves the right to disable any Organisation account that violates these Terms or misuses the Platform.',
        ],
      },
      {
        heading: '5. Acceptable Use',
        body: [
          'You agree not to use the Platform to upload, distribute, or transmit any content that is unlawful, defamatory, infringing, obscene, or harmful.',
          'You must not attempt to reverse-engineer, scrape, disrupt, or gain unauthorised access to any part of the Platform or its infrastructure.',
          'You must not use the Platform to conduct any automated testing, stress testing, or bot-driven activity without prior written consent from RADTAR.',
        ],
      },
      {
        heading: '6. Intellectual Property',
        body: [
          `All content, trademarks, logos, and software on ${APP_NAME} are the property of RADTAR or its licensors. You may not reproduce, redistribute, or exploit any part of the Platform without explicit written permission.`,
          'AI-generated content (questions, reports, feedback) produced by the Platform is made available to you for personal or organisational use within the Platform only.',
        ],
      },
      {
        heading: '7. Disclaimers',
        body: [
          'The Platform is provided on an "as is" and "as available" basis. We do not guarantee that the Platform will be error-free, uninterrupted, or that AI-generated content will be perfectly accurate.',
          'RADTAR Innovations Pvt Ltd is not responsible for the accuracy of exam content or study materials uploaded by Organisations.',
        ],
      },
      {
        heading: '8. Limitation of Liability',
        body: [
          'To the fullest extent permitted by law, RADTAR Innovations Pvt Ltd shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of or inability to use the Platform.',
        ],
      },
      {
        heading: '9. Changes to Terms',
        body: [
          'We may update these Terms from time to time. Continued use of the Platform after changes are posted constitutes acceptance of the updated Terms. We will notify users of significant changes via email or in-platform notice.',
        ],
      },
      {
        heading: '10. Contact',
        body: [
          'If you have any questions about these Terms, please contact us at support@radtar.com.',
        ],
      },
    ],
  },

  privacy: {
    title:        'Privacy Policy',
    effective:    'Effective Date: July 6, 2026',
    intro:        `RADTAR Innovations Pvt Ltd ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use ${APP_NAME}.`,
    sections: [
      {
        heading: '1. Information We Collect',
        body: [
          'When you register, we collect your name, email address, password (hashed), role, and optionally your mobile number and organisation invite code.',
          'When you use the Platform, we collect usage data including exam attempts, scores, performance metrics, and session activity.',
          'Organisations may upload PDF documents to the Platform. These are stored and processed solely to generate exam content.',
        ],
      },
      {
        heading: '2. How We Use Your Information',
        body: [
          'To create and manage your account and authenticate your identity.',
          'To provide Platform features including exam generation, performance tracking, and personalised study reports.',
          'To send transactional emails such as OTP verification, account approval notifications, and password resets.',
          'To monitor Platform usage, detect fraud, and maintain the security and integrity of the system.',
        ],
      },
      {
        heading: '3. Data Storage & Security',
        body: [
          'Your data is stored securely in cloud-hosted databases with access controls and encryption at rest and in transit.',
          'Passwords are hashed using bcrypt and are never stored or transmitted in plain text.',
          'We take reasonable technical and organisational measures to protect your data. However, no system is completely secure, and we cannot guarantee absolute security.',
        ],
      },
      {
        heading: '4. Sharing of Information',
        body: [
          'We do not sell, rent, or share your personal data with third parties for marketing purposes.',
          'We may share data with trusted service providers (e.g. cloud hosting, email delivery) strictly to operate the Platform, under confidentiality obligations.',
          'We may disclose information if required by law, court order, or to protect the rights and safety of RADTAR or its users.',
        ],
      },
      {
        heading: '5. Your Rights',
        body: [
          'You have the right to access, update, or request deletion of your personal data. To exercise these rights, contact us at support@radtar.com.',
          'You may request account deletion at any time. Deletion will remove your personal data from active systems, subject to legal retention requirements.',
        ],
      },
      {
        heading: '6. Cookies & Local Storage',
        body: [
          'The Platform uses local storage to maintain your session (authentication token) and theme preferences. No third-party tracking cookies are used.',
        ],
      },
      {
        heading: '7. Children\'s Privacy',
        body: [
          'The Platform is not intended for use by individuals under the age of 13. We do not knowingly collect personal data from children. If you believe a child has registered, please contact us immediately.',
        ],
      },
      {
        heading: '8. Changes to This Policy',
        body: [
          'We may update this Privacy Policy from time to time. We will notify you of significant changes via email or in-platform notice. Continued use of the Platform constitutes acceptance of the updated Policy.',
        ],
      },
      {
        heading: '9. Contact',
        body: [
          'If you have any questions or concerns about this Privacy Policy, please contact us at support@radtar.com.',
        ],
      },
    ],
  },
}
